import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/common/Button';
import EditProfileDialog from '../components/profile/EditProfileDialog';
import { toast } from 'react-toastify';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import './ProfilePage.css';

// Define libraries as a constant to prevent re-renders
const GOOGLE_MAPS_LIBRARIES: ("places")[] = ['places'];

const ProfilePage: React.FC = () => {
  const { currentUser, userProfile, logout, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // Debug logging
  console.log('[ProfilePage] Component state:', {
    isLoaded,
    hasCurrentUser: !!currentUser,
    hasUserProfile: !!userProfile,
    apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? 'Set' : 'Missing',
  });

  const handleRefresh = async () => {
    try {
      await refreshProfile();
      toast.success('Profile refreshed');
    } catch (error) {
      toast.error('Failed to refresh profile');
    }
  };

  useEffect(() => {
    if (currentUser && !userProfile) {
      handleRefresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const handleLogoutConfirm = async () => {
    try {
      await logout();
      // Stay on profile page after logout - it will show guest view
      toast.success('You have been logged out');
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  // Guest View (if not authenticated)
  if (!currentUser) {
    return (
      <div className="profile-page">
        <div className="profile-header">
          <h1 className="profile-title">Profile</h1>
        </div>
        <div className="guest-prompt">
          <div className="guest-prompt-icon">👤</div>
          <h2 className="guest-prompt-title">Welcome, Guest!</h2>
          <p className="guest-prompt-text">
            Sign in to view your profile, track orders, and checkout faster.
          </p>
          <div className="guest-prompt-buttons">
            <Button 
              onClick={() => navigate('/login')} 
              className="guest-login-button"
              style={{
                backgroundColor: '#B8860B',
                color: 'white',
                padding: '14px 32px',
                fontSize: '16px',
                fontWeight: 600,
                borderRadius: '30px',
                border: 'none',
                width: '100%',
                marginBottom: '12px'
              }}
            >
              Log In
            </Button>
            <Button 
              onClick={() => navigate('/register')} 
              variant="outline"
              className="guest-register-button"
              style={{
                backgroundColor: 'transparent',
                color: '#B8860B',
                padding: '14px 32px',
                fontSize: '16px',
                fontWeight: 600,
                borderRadius: '30px',
                border: '2px solid #B8860B',
                width: '100%'
              }}
            >
              Create Account
            </Button>
          </div>
          <p className="guest-prompt-note">
            You can browse and add items to cart without an account.
            <br />
            Sign in is only required at checkout.
          </p>
        </div>
      </div>
    );
  }

  // Loading state
  if (!userProfile) {
    return (
      <div className="profile-page">
        <div className="profile-loading">Loading profile...</div>
      </div>
    );
  }

  const address = userProfile.shippingAddress;
  const hasCoordinates = address?.latitude && address?.longitude;

  console.log('[ProfilePage] Address data:', {
    hasAddress: !!address,
    fullAddressObject: address,
    addressKeys: address ? Object.keys(address) : [],
    hasCoordinates,
    latitude: address?.latitude,
    longitude: address?.longitude,
    street: address?.street,
    city: address?.city,
    state: address?.state,
    zipCode: address?.zipCode,
    country: address?.country,
  });

  // Check if address exists and stringify it for full visibility
  if (address) {
    console.log('[ProfilePage] Address JSON:', JSON.stringify(address, null, 2));
  }

  return (
    <div className="profile-page">
      {/* Profile Header */}
      <div className="profile-header">
        <h1 className="profile-title">Profile</h1>
        <button className="edit-button" onClick={() => setShowEditDialog(true)}>
          <img src="/assets/images/edit.png" alt="Edit" className="edit-icon" />
        </button>
      </div>

      {/* User Information Section */}
      <div className="user-info-section">
        <h2 className="user-name">{userProfile.name}</h2>
        <p className="user-email">{userProfile.email}</p>
      </div>

      {/* Contact Information Card */}
      <div className="info-card">
        <h3 className="card-heading">Contact Information</h3>
        <div className="info-field">
          <label className="info-label">Email</label>
          <p className="info-value">{userProfile.email}</p>
        </div>
        <div className="info-field">
          <label className="info-label">Phone</label>
          <p className="info-value">{userProfile.phone || 'Not provided'}</p>
        </div>
      </div>

      {/* Shipping Address Card */}
      <div className="info-card">
        <h3 className="card-heading">Shipping Address</h3>

        {/* Google Map Widget */}
        <div className="map-container">
          {!isLoaded ? (
            <div className="map-placeholder">
              <div style={{ textAlign: 'center', color: '#9CA3AF' }}>Loading map...</div>
            </div>
          ) : hasCoordinates ? (
            <GoogleMap
              mapContainerClassName="map-widget"
              center={{
                lat: address.latitude!,
                lng: address.longitude!,
              }}
              zoom={15}
              options={{
                disableDefaultUI: true,
                gestureHandling: 'none',
                zoomControl: false,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
                clickableIcons: false,
              }}
            >
              <Marker
                position={{
                  lat: address.latitude!,
                  lng: address.longitude!,
                }}
                title="Shipping Address"
              />
            </GoogleMap>
          ) : (
            <div className="map-placeholder">
              <div className="map-placeholder-icon">🗺️</div>
              <div className="map-placeholder-text">📍</div>
            </div>
          )}
        </div>
        {!hasCoordinates && (
          <div style={{
            marginTop: '8px',
            padding: '8px 12px',
            backgroundColor: '#FEF3C7',
            borderRadius: '6px',
            fontSize: '13px',
            color: '#92400E',
          }}>
            💡 <strong>Tip:</strong> Click Edit and use the address search to add map location
          </div>
        )}

        {/* Address Text */}
        <div className="address-text">
          {address ? (
            <>
              {address.street && <div>{address.street}</div>}
              {(address.city || address.state) && (
                <div>
                  {address.city}
                  {address.state && `, Co. ${address.state}`}
                </div>
              )}
              {address.zipCode && <div>{address.zipCode}</div>}
              {address.country && <div>{address.country}</div>}
            </>
          ) : (
            <p className="address-empty">No address provided</p>
          )}
        </div>
      </div>

      {/* Logout Button */}
      <div className="logout-section">
        <button className="logout-button" onClick={() => setShowLogoutDialog(true)}>
          <img src="/assets/images/sign-out.png" alt="Sign out" className="logout-icon" />
          <span>Logout</span>
        </button>
        <button
          className="delete-account-link"
          onClick={() => navigate('/delete-account')}
        >
          Delete Account
        </button>
      </div>

      {/* Edit Profile Dialog */}
      {showEditDialog && (
        <EditProfileDialog
          userProfile={userProfile}
          onClose={() => setShowEditDialog(false)}
          onSave={() => {
            setShowEditDialog(false);
            handleRefresh();
          }}
        />
      )}

      {/* Logout Confirmation Dialog */}
      {showLogoutDialog && (
        <div className="modal-overlay" onClick={() => setShowLogoutDialog(false)}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Logout</h2>
            <p className="modal-message">Are you sure you want to logout?</p>
            <div className="modal-actions">
              <button
                className="modal-button modal-button-cancel"
                onClick={() => setShowLogoutDialog(false)}
              >
                Cancel
              </button>
              <button
                className="modal-button modal-button-logout"
                onClick={handleLogoutConfirm}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
